import { useEffect, type ReactNode } from 'react';
import { Button } from './Button';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Roter Bestätigungs-Button für destruktive Aktionen. */
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Schlanker, gebrandeter Bestätigungsdialog (Ersatz für `window.confirm`).
 * Barrierearm: role=dialog/aria-modal, Fokus auf Bestätigen beim Öffnen, Esc schließt,
 * Enter bestätigt, Klick auf den Backdrop schließt.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  danger = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Enter') {
        // Nicht bestätigen, während in einem Eingabefeld getippt wird (Footgun-Schutz
        // für künftige Nutzung in Formular-Kontexten).
        const el = document.activeElement as HTMLElement | null;
        const tag = el?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) {
          return;
        }
        e.preventDefault();
        onConfirm();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, onConfirm]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex animate-fade-in items-center justify-center bg-black/40 p-4 backdrop-blur-[1px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        className="w-full max-w-sm animate-pop-in rounded-card bg-white p-5 shadow-pop"
      >
        <h2 id="confirm-dialog-title" className="text-sm font-semibold text-kalkuel-ink">
          {title}
        </h2>
        {message && <div className="mt-2 text-sm text-kalkuel-muted">{message}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {cancelLabel}
          </Button>
          <Button autoFocus variant={danger ? 'danger' : 'primary'} size="sm" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
